/* Header elements.
   <site-header>   solid / transparent state while scrolling, hide on scroll, closes the menu drawer on wide screens.
   <header-menu>   desktop dropdowns and mega menus (hover or click).
   <menu-drawer>   sliding panels of the phone menu. */

const Theme = window.Theme || {};

/* ---------- Site header ---------- */
class SiteHeader extends HTMLElement {
  connectedCallback() {
    this.wrapper = this.closest('.shopify-section');
    this.lastY = window.scrollY;
    this.ticking = false;
    this.hideOnScroll = this.dataset.hideOnScroll === 'true';
    this.onScroll = () => {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => { this.update(); this.ticking = false; });
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });

    // The inline navigation only exists from 1150px. Close the drawer when the window grows past it.
    this.wide = matchMedia('(min-width: 1150px)');
    this.onWide = () => {
      if (this.wide.matches && this.dataset.layout !== 'drawer') Theme.closeDialog?.('header-menu-drawer');
    };
    this.wide.addEventListener('change', this.onWide);

    this.onFocusIn = () => this.reveal();
    this.addEventListener('focusin', this.onFocusIn);
    this.update();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    this.wide?.removeEventListener('change', this.onWide);
    this.removeEventListener('focusin', this.onFocusIn);
    this.wrapper?.classList.remove('is-hidden');
  }

  get busy() {
    return this.classList.contains('is-open') || this.matches(':focus-within') || document.body.classList.contains('is-locked');
  }

  reveal() {
    this.wrapper?.classList.remove('is-hidden');
  }

  update() {
    const y = Math.max(0, window.scrollY);
    this.classList.toggle('is-scrolled', y > 10);
    if (this.hideOnScroll && this.wrapper) {
      const delta = y - this.lastY;
      if (y <= 10 || delta < -4) this.reveal();
      else if (delta > 4 && y > this.offsetHeight && !this.busy) this.wrapper.classList.add('is-hidden');
    }
    this.lastY = y;
  }
}
if (!customElements.get('site-header')) customElements.define('site-header', SiteHeader);

/* ---------- Dropdown navigation ---------- */
class HeaderMenu extends HTMLElement {
  connectedCallback() {
    this.header = this.closest('site-header');
    this.items = [...this.querySelectorAll('.header__item--parent')];
    this.hoverMode = this.dataset.trigger === 'hover';
    this.fine = matchMedia('(hover: hover) and (pointer: fine)');
    this.timers = new Map();

    this.items.forEach((item) => {
      const link = item.querySelector(':scope > [data-parent-link]');
      if (!link) return;
      link.addEventListener('click', (event) => {
        // In hover mode on a mouse the link simply navigates. Otherwise the first click opens the dropdown.
        if (this.hoverMode && this.fine.matches) return;
        if (!item.classList.contains('is-open')) { event.preventDefault(); this.open(item); }
        else if (event.target.closest('.header__link-chevron')) { event.preventDefault(); this.close(item); }
      });
      link.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          this.open(item);
          item.querySelector('.header__dropdown a')?.focus();
        }
      });
      item.addEventListener('pointerenter', (event) => {
        if (!this.hoverMode || event.pointerType !== 'mouse') return;
        clearTimeout(this.timers.get(item));
        this.open(item);
      });
      item.addEventListener('pointerleave', (event) => {
        if (!this.hoverMode || event.pointerType !== 'mouse') return;
        this.timers.set(item, setTimeout(() => this.close(item), 150));
      });
      item.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && item.classList.contains('is-open')) {
          event.stopPropagation();
          this.close(item);
          link.focus();
        }
      });
      item.addEventListener('focusout', (event) => {
        if (event.relatedTarget && !item.contains(event.relatedTarget)) this.close(item);
      });
    });

    this.onDocumentClick = (event) => { if (!this.contains(event.target)) this.closeAll(); };
    this.onCloseAll = () => this.closeAll();
    // Editor: open the mega menu of the selected block.
    this.onBlockSelect = (event) => { const item = event.target.closest?.('.header__item--parent'); if (item && this.contains(item)) this.open(item); };
    this.onBlockDeselect = () => this.closeAll();
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('dialog:open', this.onCloseAll);
    document.addEventListener('tt:navigated', this.onCloseAll);
    document.addEventListener('shopify:block:select', this.onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
  }

  disconnectedCallback() {
    this.timers?.forEach((t) => clearTimeout(t));
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('dialog:open', this.onCloseAll);
    document.removeEventListener('tt:navigated', this.onCloseAll);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    this.header?.classList.remove('is-open');
  }

  open(item) {
    this.items.forEach((other) => { if (other !== item) this.close(other, false); });
    item.classList.add('is-open');
    item.querySelector(':scope > [data-parent-link]')?.setAttribute('aria-expanded', 'true');
    this.sync();
  }

  close(item, sync = true) {
    item.classList.remove('is-open');
    item.querySelector(':scope > [data-parent-link]')?.setAttribute('aria-expanded', 'false');
    if (sync) this.sync();
  }

  closeAll() {
    this.items.forEach((item) => this.close(item, false));
    this.sync();
  }

  sync() {
    this.header?.classList.toggle('is-open', this.items.some((item) => item.classList.contains('is-open')));
  }
}
if (!customElements.get('header-menu')) customElements.define('header-menu', HeaderMenu);

/* ---------- Menu drawer ---------- */
class MenuDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.closest('dialog');
    this.panels = [...this.querySelectorAll('[data-panel]')];
    this.back = this.querySelector('[data-back]');
    this.backLabel = this.querySelector('[data-back-label]');
    this.defaultBackLabel = this.backLabel ? this.backLabel.textContent : '';
    this.stack = ['root'];

    this.onClick = (event) => {
      const open = event.target.closest('[data-panel-open]');
      if (open && this.contains(open)) {
        this.stack.push(open.dataset.panelOpen);
        this.render(true);
        return;
      }
      if (event.target.closest('[data-back]')) {
        if (this.stack.length > 1) this.stack.pop();
        this.render(true);
      }
    };
    this.addEventListener('click', this.onClick);

    // Back to the first level whenever the drawer closes.
    this.onClose = () => { this.stack = ['root']; this.render(false); };
    this.dialog?.addEventListener('close', this.onClose);
    this.render(false);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.dialog?.removeEventListener('close', this.onClose);
  }

  panel(id) {
    return this.panels.find((p) => p.dataset.panel === id);
  }

  render(moveFocus) {
    const current = this.stack[this.stack.length - 1];
    this.panels.forEach((panel) => {
      const id = panel.dataset.panel;
      const index = this.stack.indexOf(id);
      let state = 'future';
      if (id === current) state = 'current';
      else if (index > -1) state = 'past';
      panel.dataset.state = state;
      panel.inert = state !== 'current';
      if (state === 'future') panel.scrollTop = 0;
    });
    this.querySelectorAll('[data-panel-open]').forEach((button) => button.setAttribute('aria-expanded', String(this.stack.includes(button.dataset.panelOpen))));
    if (this.back) {
      const isRoot = this.stack.length === 1;
      this.back.hidden = isRoot;
      if (this.backLabel) {
        const parent = this.panel(this.stack[this.stack.length - 2]);
        this.backLabel.textContent = parent && parent.dataset.title ? parent.dataset.title : this.defaultBackLabel;
      }
    }
    if (moveFocus) {
      const target = this.panel(current);
      requestAnimationFrame(() => (target?.querySelector('a, button') || this.back)?.focus({ preventScroll: true }));
    }
  }
}
if (!customElements.get('menu-drawer')) customElements.define('menu-drawer', MenuDrawer);
